import { useCallback, useEffect, VFC } from 'react';
import styled from 'styled-components';
import { Navigate } from 'react-router-dom';
import { useUser } from '../hooks/useUser';
import { ReactComponent as FacebookLogo } from '../static/facebook.svg';
import { ReactComponent as AdvaniaLogo } from '../static/advanialogowhite.svg';
import { ReactComponent as Globe } from '../static/globe.svg';
import * as apiClient from '../utils/apiClient';
import socialbackground from '../static/backgroundlogin.svg';

const PageWrap = styled.div`
    height: 100%;
    max-height: 100vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: url(${socialbackground});
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center center;
`;

const InnerContent = styled.div`
    display: flex;
    height: 100%;
    flex-direction: column;
    margin-inline: clamp(1rem, 11vw, 40rem);
    margin-top: clamp(5rem, 30vh, 40rem);
    align-items: flex-start;
    max-width: 600px;
    
    span {
        gap: ${props => props.theme.spacing[2]};
        display: flex;
        align-items: center;
        margin-top: ${props => props.theme.spacing[0]};
        margin-bottom: ${props => props.theme.spacing[5]};
        padding: 0px;
        font-size: ${props => props.theme.fontSize[6]};
        font-weight: normal;
        font-size: 66px;
        line-height: 85px;
        color: ${props => props.theme.colors.white};

    }
    h2 {
        margin-top: ${props => props.theme.spacing[0]};
        margin-bottom: ${props => props.theme.spacing[9]};
        font-size: ${props => props.theme.fontSize[6]};
        font-weight: normal;
        font-size: 36px;
        font-family: adelle-sans;
        line-height: 44px;
        color: rgba(255, 255, 255, 0.85);
    }
    @media (max-width: 480px) {
        margin-inline: 1.5rem;
        margin-bottom: 3rem;
        span {
            font-size: 36px;
            line-height: 44px;
        }

        h2 {
            font-size: 24px;
            line-height: 32px;
        }
    }
`;

const FBLoginButton = styled.button`
    background-color: transparent;
    color: ${props => props.theme.colors.white};
    font-weight: bold;
    border: 1.5px solid;
    border-radius: ${props => props.theme.radius.full};
    padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[5]};
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: ${props => props.theme.fontSize[2]};
    font-weight: normal;
    margin-bottom: ${props => props.theme.spacing[2]};
    transform: translateY(0);
    will-change: transform, box-shadow;
    transition: transform .25s ease, box-shadow .25s ease;
    font-family: adelle-sans;

    &:hover,
    &:focus,
    &:active {
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        transform: translateY(-2px);
    }

    .light {
        font-weight: normal;
    }
`;

const FBButtonIcon = styled(FacebookLogo)`
    width: 1.875rem;
    margin-right: ${props => props.theme.spacing[4]};
`;

const FooterWrap = styled.div`
    a {
        color: rgba(255, 255, 255, 0.74);
        text-decoration: none;
        font-size: 18px;
        font-weight: normal;
        line-height: 28px;
        font-family: adelle-sans;
        &:hover {
            text-decoration: underline;
        }
    }
    padding: 0px;
    display: inline-flex;
    gap: ${props => props.theme.spacing[3]};
`;

const SiteLogo = styled(AdvaniaLogo)`
    width: 11rem;
`;

const LogoWrap = styled.div`
    gap: ${props => props.theme.spacing[2]};
    color: rgba(255, 255, 255, 0.74);
    text-decoration: none;
    font-size: 18px;
    font-weight: normal;
    line-height: 28px;
    font-family: adelle-sans;
    display: flex;
    align-items: center;
    margin: 0 auto;
    margin-bottom: clamp(2rem, 16vh, 40rem);
`;

const GlobeIcon = styled(Globe)`
    width: 4.19rem;
`;

interface LoginProps {
    from?: string | null;
}

export const Login: VFC<LoginProps> = ({ from }) => {
    const { performLogin, user, setUser } = useUser();

    const checkUser = useCallback(async () => {
        try {
            const currentUser = await apiClient.currentUser();

            if (currentUser != null) {
                setUser(currentUser);
            }
        } catch (error) {
            console.log("Fel vid inloggning");
        }
    }, [ setUser ]);

    useEffect(() => {
        checkUser();
    }, [ checkUser ]);

    if (user != null) {
        return (<Navigate to={from || '/'} />);
    }

    return (
        <PageWrap>
            <InnerContent>
                <span>
                    <GlobeIcon />
                    Social Connect
                </span>
                <h2>Koppla upp din webbplats mot sociala medier!</h2>
                <FBLoginButton onClick={() => performLogin(from)}>
                    <FBButtonIcon />
                    Logga in med Facebook
                </FBLoginButton>
                <FooterWrap>
                    <a href="https://www.advania.se/om-advania/integritetsinformation/" target="_blank">
                        Integritetspolicy
                    </a>
                    <a href="javascript:Didomi.preferences.show()">Hantera kakor</a>
                </FooterWrap>
            </InnerContent>
            <LogoWrap>
                Powered by
                <SiteLogo />
            </LogoWrap>
        </PageWrap>
    );
}
